// ============================================================
// SlotConfirmDialog — confirm-before-change modal
// ============================================================
// No silent relationship changes. Every Track / Block / Untrack / Unblock
// states the consequence BEFORE applying. Tracking and blocking are included
// in the selected location plan; these actions never change the monthly bill.
// The recolor + slot write happen only after onConfirm.

import { useState } from 'react'
import { TRACK_STATE_COLORS } from './mapStyle'

export type ConfirmAction = 'track' | 'block' | 'untrack' | 'unblock'

/** A fingerprint-identical sibling pin near the acted pin (§3 Phase 4). */
export interface SameMenuPin {
  name: string
  city: string | null
  distance_km: number | null
}

export interface SlotConfirmData {
  action: ConfirmAction
  rivalName: string
  /** The location the slot applies to (single-location actions). */
  locationName?: string
  /** Unblock only — true when this releases the org's LAST block on the
   *  rival (fires the sales follow-up); drives the consequence copy. */
  isLastBlock?: boolean
  /** Block-all only — how many of the operator's locations are affected. */
  locationCount?: number
  /** Fingerprint-identical sibling pins near the acted pin (§3 Phase 4 — set on
   *  add actions only). When present, the dialog surfaces an opt-in "this exact
   *  menu also appears at N nearby pins — treat as one rival?" — default OFF, no
   *  auto-merge ever. Opting in does not change billing; it acknowledges that
   *  the same menu appears at N pins and should count as one rival. */
  sameMenuPins?: SameMenuPin[]
}

const isHighStakes = (a: ConfirmAction) => a === 'block' || a === 'unblock'

const TITLE: Record<ConfirmAction, (r: string) => string> = {
  track: (r) => `Track ${r}`,
  block: (r) => `Block ${r}`,
  untrack: (r) => `Untrack ${r}`,
  unblock: (r) => `Cancel block on ${r}`,
}

const CONFIRM_LABEL: Record<ConfirmAction, string> = {
  track: 'Track rival',
  block: 'Block this rival',
  untrack: 'Untrack rival',
  unblock: 'Cancel this block',
}

/** Mono number span (BRAND.md: numbers/prices always monospace). */
function Money({ children, color }: { children: string; color?: string }) {
  return <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: color ?? 'inherit' }}>{children}</span>
}

export function SlotConfirmDialog({
  data,
  busy,
  onConfirm,
  onCancel,
}: {
  data: SlotConfirmData
  busy: boolean
  onConfirm: (treatAsOne: boolean) => void
  onCancel: () => void
}) {
  const { action, rivalName, locationName, isLastBlock, locationCount, sameMenuPins } = data
  // Opt-in "treat fingerprint-identical pins as one rival" — default OFF (§3
  // Phase 4: never auto-merge). Only meaningful when adding monitoring.
  const [treatAsOne, setTreatAsOne] = useState(false)
  if (!['track', 'untrack'].includes(action)) return null
  const showSameMenu = (action === 'track' || action === 'block') && (sameMenuPins?.length ?? 0) > 0
  const accent = isHighStakes(action) ? TRACK_STATE_COLORS.blocked : TRACK_STATE_COLORS.tracking
  const confirmColor = action === 'block' ? TRACK_STATE_COLORS.blocked
    : action === 'unblock' ? 'var(--accent-alert, #d4537e)'
    : action === 'track' ? TRACK_STATE_COLORS.tracking
    : 'var(--text-2)'
  const confirmTextColor = action === 'block' ? '#0d0f11' : '#fff'

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'absolute', inset: 0, zIndex: 50, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(1.5px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 360, maxWidth: '90%', background: 'var(--surface-solid, var(--surface))',
          border: '1px solid var(--border)', borderRadius: 10, padding: '18px 20px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.4)', fontFamily: 'var(--sans)',
          borderTop: `3px solid ${accent}`,
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 10 }}>
          {TITLE[action](rivalName)}
        </div>

        <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6, marginBottom: 14 }}>
          <div>
            Included in your location plan — this action does not change your monthly bill.
          </div>

          {/* High-stakes consequence — block is STORE-level (this rival store
              won't see this location's data), not a statewide brand block. */}
          {action === 'block' && (
            <div style={{ marginTop: 6 }}>
              {locationCount && locationCount > 1
                ? `${rivalName} won't be able to see any of your locations' data.`
                : `${rivalName} won't be able to see ${locationName ?? 'your location'}'s data.`}
            </div>
          )}
          {action === 'unblock' && isLastBlock && (
            <div style={{ marginTop: 6 }}>
              Canceling this block will re-add {rivalName} to our prospect list. Our team typically
              follows up within 24–48 hours.
            </div>
          )}

          {/* Same-menu opt-in (§3 Phase 4) — empirical, fingerprint-verified.
              Default OFF; never auto-merges. In light mode this is an honest
              acknowledgment, not a billing change. */}
          {showSameMenu && (
            <label style={{
              display: 'flex', gap: 9, alignItems: 'flex-start', marginTop: 12, padding: '10px 11px',
              background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 7,
              cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={treatAsOne}
                onChange={(e) => setTreatAsOne(e.target.checked)}
                style={{ marginTop: 1, accentColor: accent, cursor: 'pointer', flex: '0 0 auto' }}
              />
              <span style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5 }}>
                This exact menu also appears at{' '}
                <Money color="var(--text-1)">{`${sameMenuPins!.length}`}</Money>{' '}
                nearby {sameMenuPins!.length === 1 ? 'pin' : 'pins'}. Treat as one rival?
                <span style={{ display: 'block', color: 'var(--text-3)', fontSize: 11, marginTop: 4 }}>
                  {treatAsOne
                    ? `You're monitoring this menu once — the ${sameMenuPins!.length} identical ${sameMenuPins!.length === 1 ? 'pin is' : 'pins are'} the same menu, not separate rivals.`
                    : 'Left unchecked, any of these pins you set up later counts as its own rival.'}
                </span>
                <span style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginTop: 5, lineHeight: 1.5 }}>
                  {sameMenuPins!.slice(0, 3).map((s) =>
                    `${s.name}${s.distance_km != null ? ` · ${s.distance_km}km` : ''}`
                  ).join('   ·   ')}
                  {sameMenuPins!.length > 3 ? `   +${sameMenuPins!.length - 3} more` : ''}
                </span>
              </span>
            </label>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={onCancel}
            disabled={busy}
            style={{
              flex: '0 0 auto', padding: '8px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
              fontFamily: 'var(--sans)', cursor: busy ? 'default' : 'pointer',
              border: '1px solid var(--border-2)', background: 'transparent', color: 'var(--text-2)',
            }}
          >
            Keep as is
          </button>
          <button
            onClick={() => onConfirm(treatAsOne)}
            disabled={busy}
            style={{
              flex: 1, padding: '8px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700,
              fontFamily: 'var(--sans)', cursor: busy ? 'default' : 'pointer',
              border: `1px solid ${confirmColor}`, background: confirmColor, color: confirmTextColor,
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? 'Applying…' : CONFIRM_LABEL[action]}
          </button>
        </div>
      </div>
    </div>
  )
}
